import React from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Package, Truck, CheckCircle, Clock, MapPin, Phone, Mail } from 'lucide-react';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
  margin-bottom: 30px;
  padding: 8px 0;
  
  &:hover {
    color: #374151;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const OrderTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
`;

const OrderNumber = styled.div`
  font-size: 18px;
  color: #3b82f6;
  font-weight: 600;
  margin-bottom: 8px;
`;

const OrderDate = styled.div`
  color: #6b7280;
  font-size: 14px;
`;

const StatusTimeline = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid #e5e7eb;
`;

const TimelineTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 24px;
  text-align: center;
`;

const Timeline = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin: 40px 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: #e5e7eb;
    z-index: 1;
  }
`;

const TimelineStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  flex: 1;
`;

const StepIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.completed ? '#10b981' : props.current ? '#3b82f6' : '#e5e7eb'};
  color: ${props => props.completed || props.current ? 'white' : '#9ca3af'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  box-shadow: 0 0 0 4px white;
`;

const StepLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.completed ? '#10b981' : props.current ? '#3b82f6' : '#9ca3af'};
  text-align: center;
  margin-bottom: 4px;
`;

const StepTime = styled.div`
  font-size: 10px;
  color: #9ca3af;
  text-align: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: #111827;
  font-weight: 500;
  font-size: 14px;
`;

const ProductList = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
`;

const ProductItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const ProductImage = styled.div`
  width: 60px;
  height: 60px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  flex-shrink: 0;
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.div`
  font-weight: 500;
  color: #111827;
  margin-bottom: 4px;
`;

const ProductDetails = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ProductPrice = styled.div`
  font-weight: 600;
  color: #111827;
  text-align: right;
`;

const TotalSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  
  &.final {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    padding-top: 8px;
    border-top: 1px solid #e5e7eb;
    margin-top: 8px;
  }
`;

function OrderDetail() {
  const history = useHistory();
  const { orderId } = useParams();

  // 더미 주문 데이터
  const orderData = {
    id: orderId || 'ORD-001',
    date: '2025-08-09',
    status: 'shipped',
    customer: {
      name: '김철수',
      phone: '010-1234-5678',
      email: 'kim.cs@email.com'
    },
    shipping: {
      address: '서울특별시 강남구 테헤란로 123',
      detailAddress: 'AntsMarket 빌딩 10층',
      zipCode: '06142',
      method: '일반배송',
      fee: 3000
    },
    payment: {
      method: '신용카드',
      cardNumber: '**** **** **** 1234',
      amount: 92000
    },
    products: [
      {
        id: 1,
        name: '프리미엄 클래식 셔츠',
        quantity: 1,
        price: 89000,
        options: '사이즈: L, 색상: 화이트'
      }
    ]
  };

  const timeline = [
    {
      status: 'ordered',
      label: '주문접수',
      time: '08-09 14:30',
      icon: <Package size={20} />,
      completed: true
    },
    {
      status: 'confirmed',
      label: '주문확인',
      time: '08-09 15:00',
      icon: <CheckCircle size={20} />,
      completed: true
    },
    {
      status: 'shipped',
      label: '배송시작',
      time: '08-10 09:00',
      icon: <Truck size={20} />,
      completed: false,
      current: true
    },
    {
      status: 'delivered',
      label: '배송완료',
      time: '예정',
      icon: <CheckCircle size={20} />,
      completed: false
    }
  ];

  const subtotal = orderData.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
  const total = subtotal + orderData.shipping.fee;

  return (
    <Container>
      <BackButton onClick={() => history.goBack()}>
        <ArrowLeft size={16} />
        주문내역으로 돌아가기
      </BackButton>

      <Header>
        <OrderTitle>주문 상세 정보</OrderTitle>
        <OrderNumber>주문번호: {orderData.id}</OrderNumber>
        <OrderDate>주문일시: {orderData.date}</OrderDate>
      </Header>

      <StatusTimeline>
        <TimelineTitle>배송 현황</TimelineTitle>
        <Timeline>
          {timeline.map((step, index) => (
            <TimelineStep key={index}>
              <StepIcon completed={step.completed} current={step.current}>
                {step.icon}
              </StepIcon>
              <StepLabel completed={step.completed} current={step.current}>
                {step.label}
              </StepLabel>
              <StepTime>{step.time}</StepTime>
            </TimelineStep>
          ))}
        </Timeline>
      </StatusTimeline>

      <ContentGrid>
        <Card>
          <CardTitle>
            <Phone size={18} />
            주문자 정보
          </CardTitle>
          <InfoRow>
            <InfoLabel>이름</InfoLabel>
            <InfoValue>{orderData.customer.name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>전화번호</InfoLabel>
            <InfoValue>{orderData.customer.phone}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>이메일</InfoLabel>
            <InfoValue>{orderData.customer.email}</InfoValue>
          </InfoRow>
        </Card>

        <Card>
          <CardTitle>
            <MapPin size={18} />
            배송 정보
          </CardTitle>
          <InfoRow>
            <InfoLabel>배송지</InfoLabel>
            <InfoValue style={{textAlign: 'right', maxWidth: '200px'}}>
              {orderData.shipping.address}<br/>
              {orderData.shipping.detailAddress}<br/>
              ({orderData.shipping.zipCode})
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>배송방법</InfoLabel>
            <InfoValue>{orderData.shipping.method}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>배송비</InfoLabel>
            <InfoValue>₩{orderData.shipping.fee.toLocaleString()}</InfoValue>
          </InfoRow>
        </Card>
      </ContentGrid>

      <ProductList>
        <CardTitle>
          <Package size={18} />
          주문 상품
        </CardTitle>
        {orderData.products.map((product) => (
          <ProductItem key={product.id}>
            <ProductImage>
              IMG
            </ProductImage>
            <ProductInfo>
              <ProductName>{product.name}</ProductName>
              <ProductDetails>{product.options} | 수량: {product.quantity}개</ProductDetails>
            </ProductInfo>
            <ProductPrice>
              ₩{(product.price * product.quantity).toLocaleString()}
            </ProductPrice>
          </ProductItem>
        ))}
        
        <TotalSection>
          <TotalRow>
            <span>상품금액</span>
            <span>₩{subtotal.toLocaleString()}</span>
          </TotalRow>
          <TotalRow>
            <span>배송비</span>
            <span>₩{orderData.shipping.fee.toLocaleString()}</span>
          </TotalRow>
          <TotalRow className="final">
            <span>총 결제금액</span>
            <span>₩{total.toLocaleString()}</span>
          </TotalRow>
        </TotalSection>
      </ProductList>
    </Container>
  );
}

export default OrderDetail;